// File: src/pages/AgencyShiftBookingPage.jsx

import React, { useEffect, useState } from 'react';
import axios from '@/lib/axios';
import { useUserPermissions } from '@/hooks/useUserPermissions';

const AgencyShiftBookingPage = () => {
  const { canManageAgencyShifts } = useUserPermissions();

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({ agency: '', status: 'All' });

  const fetchAgencyShifts = async () => {
    try {
      const res = await axios.get('/solo-mdsh-api/staffing/agency-shifts');
      setShifts(res.data.data || []);
    } catch (err) {
      console.error('❌ Failed to fetch agency shifts:', err);
      setMessage('Error loading shifts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencyShifts();
  }, []);

  const handleConfirm = async (request_id) => {
    const booked_staff_name = prompt('Enter assigned staff name:');
    const booked_contact = prompt('Enter contact number:');

    if (!booked_staff_name || !booked_contact) return;

    try {
      await axios.post('/solo-mdsh-api/staffing/agency-shifts/confirm', {
        request_id,
        booked_staff_name,
        booked_contact,
      });
      setMessage('✅ Shift confirmed!');
      fetchAgencyShifts();
    } catch (err) {
      console.error('❌ Confirmation error:', err);
      setMessage('Failed to confirm shift.');
    }
  };

  const handleFilter = (shifts) => {
    return shifts.filter((s) => {
      const agencyMatch = !filters.agency || s.agency_name.includes(filters.agency);
      const statusMatch = filters.status === 'All' || s.status === filters.status.toLowerCase();
      return agencyMatch && statusMatch;
    });
  };

  if (!canManageAgencyShifts) {
    return (
      <div className="text-white p-8">
        <h1 className="text-2xl font-bold mb-4">🚦 Agency Shift Management</h1>
        <p className="text-red-400 font-semibold">
          You do not have permission to access agency shift booking.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen p-8 text-white">
      <h1 className="text-3xl font-bold mb-6">🚦 Agency Shift Management</h1>

      {message && <div className="mb-4 text-yellow-400 font-semibold">{message}</div>}

      <div className="mb-6 flex gap-4">
        <input
          className="bg-card p-2 rounded text-white"
          placeholder="Filter by agency name"
          value={filters.agency}
          onChange={(e) => setFilters({ ...filters, agency: e.target.value })}
        />
        <select
          className="bg-card p-2 rounded text-white"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option>All</option>
          <option>Pending</option>
          <option>Confirmed</option>
        </select>
      </div>

      {loading ? (
        <p>Loading agency shifts...</p>
      ) : handleFilter(shifts).length === 0 ? (
        <p>No agency shifts match your criteria.</p>
      ) : (
        <div className="overflow-x-auto border border-neutral-700 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-neutral-800">
              <tr>
                <th className="p-3 text-left">Event</th>
                <th>Role</th>
                <th>Time</th>
                <th>Agency</th>
                <th>Status</th>
                <th>Cost Centre</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {handleFilter(shifts).map((shift) => (
                <tr
                  key={shift.request_id}
                  className="border-t border-neutral-700 hover:bg-neutral-800"
                >
                  <td className="p-3">{shift.event_name}</td>
                  <td>{shift.role}</td>
                  <td>
                    {new Date(shift.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                    {new Date(shift.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td>{shift.agency_name}</td>
                  <td>
                    <span
                      className={`px-2 py-1 text-xs rounded font-medium ${
                        shift.status === 'confirmed'
                          ? 'bg-green-700 text-green-200'
                          : 'bg-yellow-700 text-yellow-200'
                      }`}
                    >
                      {shift.status.toUpperCase()}
                    </span>
                  </td>
                  <td>{shift.cost_centre_name || 'N/A'}</td>
                  <td className="text-right pr-4">
                    {shift.status === 'pending' && (
                      <button
                        onClick={() => handleConfirm(shift.request_id)}
                        className="bg-blue-700 hover:bg-blue-800 px-3 py-1 rounded text-sm"
                      >
                        Assign Staff
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgencyShiftBookingPage;
